import React, { createContext, useCallback, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getReducedMotion } from "../lib/motionPref";

const Ctx = createContext(null);
export const useQuantum = () => useContext(Ctx);

const Tunnel = () => (
  <motion.div className="fixed inset-0 z-[90] bg-[#05050A] flex items-center justify-center overflow-hidden"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} data-testid="quantum-tunnel">
    {Array.from({ length: 9 }).map((_, i) => (
      <motion.div key={i} className="absolute rounded-full border"
        style={{ borderColor: ["#00F0FF", "#B026FF", "#FFD700"][i % 3], boxShadow: `0 0 24px ${["#00F0FF", "#B026FF", "#FFD700"][i % 3]}` }}
        initial={{ width: 10, height: 10, opacity: 0.9 }} animate={{ width: 2400, height: 2400, opacity: 0 }}
        transition={{ duration: 0.9, delay: i * 0.07, ease: "easeIn" }} />
    ))}
  </motion.div>
);

const Collapse = ({ label }) => (
  <motion.div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="wave-collapse">
    <div className="relative w-64 h-64">
      {Array.from({ length: 7 }).map((_, i) => {
        const a = (i / 7) * Math.PI * 2;
        return (
          <motion.span key={i} className="absolute left-1/2 top-1/2 w-3 h-3 rounded-full"
            style={{ background: ["#00F0FF", "#B026FF", "#FFD700"][i % 3], boxShadow: "0 0 16px currentColor" }}
            initial={{ x: Math.cos(a) * 120, y: Math.sin(a) * 120, opacity: 0.4 }}
            animate={{ x: [Math.cos(a) * 120, 0], y: [Math.sin(a) * 120, 0], opacity: [0.4, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }} />
        );
      })}
      <motion.div className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-white"
        animate={{ scale: [0.6, 1.4, 0.6], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.4, repeat: Infinity }}
        style={{ boxShadow: "0 0 60px #fff" }} />
    </div>
    <p className="font-mono-label mt-6 text-white/70">{label}</p>
  </motion.div>
);

export const QuantumProvider = ({ children }) => {
  const navigate = useNavigate();
  const [tunnel, setTunnel] = useState(false);
  const [collapse, setCollapse] = useState(null);

  const go = useCallback((path) => {
    if (getReducedMotion()) return navigate(path);
    setTunnel(true);
    setTimeout(() => { navigate(path); setTimeout(() => setTunnel(false), 250); }, 650);
  }, [navigate]);

  const withCollapse = useCallback(async (label, fn) => {
    setCollapse(label);
    try { return await fn(); } finally { setCollapse(null); }
  }, []);

  return (
    <Ctx.Provider value={{ go, withCollapse }}>
      {children}
      <AnimatePresence>{tunnel && <Tunnel key="t" />}{collapse && <Collapse key="c" label={collapse} />}</AnimatePresence>
    </Ctx.Provider>
  );
};
